import { useEffect, useState } from 'react';

import EmailIcon from 'icons/email.svg?react';
import { toast } from 'material-react-toastify';

import Popup from '@/components/Popup/PopUp/PopUp';
import Button from '@/components/shared/Button/Button';
import { Text } from '@/components/shared/Text/Text';
import { TextField } from '@/components/shared/TextField/TextField';
import { useHubStore } from '@/lib/store/hubStore';

import { useUpdateUserEmail } from '../../hooks/useUpdateUserEmail';

interface EditEmailModalProps {
  isPopupOpen: boolean;
  closePopup: () => void;
  userEmail: string;
}

const EditEmailModal = ({ isPopupOpen, closePopup, userEmail }: EditEmailModalProps) => {
  const user = useHubStore((state) => state.searchUserData);
  const [email, setEmail] = useState<string>(userEmail);
  const updateEmailMutation = useUpdateUserEmail();

  const handleChangeEmail = async () => {
    if (!user?.id) {
      toast.error('User ID is missing');
      return;
    }

    updateEmailMutation.mutate(
      { userId: user.id, email },
      {
        onSuccess: () => {
          closePopup();
          toast.success(`User's email was successfully changed`);
        },
        onError: (error) => {
          closePopup();
          toast.error(error.message);
        },
      },
    );
  };

  useEffect(() => {
    setEmail(userEmail);
  }, [userEmail]);

  return (
    <Popup isOpen={isPopupOpen} handleClose={closePopup}>
      <div className="absolute top-[20px] left-[12px] rounded-lg bg-[#F2F2F2] p-2">
        <EmailIcon />
      </div>
      <div className="max-w-[340px] ml-12">
        <Text.Bigger className="mb-2" weight="bold">
          Email change request
        </Text.Bigger>
        <Text.Regular>
          Please enter the email address you need to change in the field below:
        </Text.Regular>
        <TextField
          startIcon={<EmailIcon className="opacity-25" />}
          className="my-4"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={updateEmailMutation.isPending}
        />

        <div className="flex justify-center items-center gap-2">
          <Button
            label={updateEmailMutation.isPending ? 'Updating...' : 'Change Email'}
            size="s"
            className="!w-44"
            onClick={handleChangeEmail}
            prefixIcon={<img alt={'check'} src={'/icons/check.svg'} />}
            disabled={updateEmailMutation.isPending || email === userEmail}
          />
          <Button
            prefixIcon={<img alt={'close'} src={'/icons/close_outline.svg'} />}
            label="Cancel"
            variant="transparent"
            onClick={closePopup}
            className="!w-28"
            size="s"
            disabled={updateEmailMutation.isPending}
          />
        </div>
      </div>
    </Popup>
  );
};

export default EditEmailModal;
